import { FileSpreadsheet, LayoutTemplate } from 'lucide-react';
import type { TemplateEditorMode } from '../templateEditor/types';

export interface TemplateModeGateProps {
  /**
   * Called when the user picks a mode. The parent is expected to patch
   * `state.mode` with the chosen value (via `updateState({ mode })`) so
   * the editor transitions out of the gate and into the tabbed UI.
   */
  onChoose: (mode: Exclude<TemplateEditorMode, null>) => void;
  /**
   * Optional back handler — typically navigates the user back to the
   * template list so they can cancel out of the editor entirely.
   */
  onCancel?: () => void;
}

/**
 * Initial "authoring mode" gate shown when a NEW template is opened in
 * the editor. Presents a binary choice:
 *
 *   - Bound  → the user has (or will upload) a sample file and will map
 *              concrete source columns to target fields in the editor.
 *   - Shell  → the user is creating a reusable blueprint; mapping is
 *              deferred to import time. The editor hides Field Mappings.
 *
 * Once chosen, the choice drives tab visibility and validation for the
 * rest of the editor session. The choice is also derived automatically
 * when hydrating an existing template (so the gate is only shown for
 * net-new authoring).
 */
export default function TemplateModeGate({ onChoose, onCancel }: TemplateModeGateProps) {
  return (
    <div className="h-full w-full flex items-start justify-center overflow-y-auto bg-gray-50">
      <div className="max-w-3xl w-full px-6 py-10 space-y-6">
        <header className="space-y-2">
          <h2 className="text-lg font-semibold text-gray-900">How would you like to author this template?</h2>
          <p className="text-sm text-gray-600">
            Import templates can either carry a concrete set of field mappings (derived from a sample file)
            or stay as <strong>shells</strong>—blueprints whose mappings are filled in at import time. Pick
            the style that fits the recurring file you're importing.
          </p>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Bound option */}
          <button
            type="button"
            data-testid="template-mode-bound"
            onClick={() => onChoose('bound')}
            className="group border-2 border-gray-200 hover:border-blue-500 bg-white rounded-xl p-5 text-left transition-colors"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-lg bg-blue-50 group-hover:bg-blue-100 transition-colors">
                <FileSpreadsheet className="h-5 w-5 text-blue-600" />
              </div>
              <h3 className="text-sm font-semibold text-gray-900">I have a sample file — map it now</h3>
            </div>
            <p className="text-xs text-gray-600 leading-relaxed">
              Upload a representative file and map each column to a target field. The template captures
              these mappings so future imports of the same file shape run with zero configuration.
            </p>
            <ul className="mt-3 space-y-1.5 text-[11px] text-gray-500">
              <li>• Best for predictable, consistently-shaped files (e.g. your county's monthly parcel export).</li>
              <li>• All editor tabs are available, including Field Mappings and the preview/dry-run tools.</li>
              <li>• Can be saved standalone or as a variant of an existing shell template.</li>
            </ul>
          </button>

          {/* Shell option */}
          <button
            type="button"
            data-testid="template-mode-shell"
            onClick={() => onChoose('shell')}
            className="group border-2 border-gray-200 hover:border-orange-500 bg-white rounded-xl p-5 text-left transition-colors"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-lg bg-orange-50 group-hover:bg-orange-100 transition-colors">
                <LayoutTemplate className="h-5 w-5 text-orange-600" />
              </div>
              <h3 className="text-sm font-semibold text-gray-900">No file yet — map at import time</h3>
            </div>
            <p className="text-xs text-gray-600 leading-relaxed">
              Define just the destination contract: what kind of data this import produces, how duplicates
              are resolved, and which target field is used to match records. Column mappings are provided
              by whoever runs the import, against whatever file they have that day.
            </p>
            <ul className="mt-3 space-y-1.5 text-[11px] text-gray-500">
              <li>• Best for recurring imports with variable file shapes (e.g. inspection rosters from multiple vendors).</li>
              <li>• Field Mappings tab is hidden; mapping happens inside the import wizard when a file is uploaded.</li>
              <li>• Each successful import can optionally be saved back as a named variant of this shell.</li>
            </ul>
          </button>
        </div>

        {onCancel && (
          <div className="pt-2">
            <button
              type="button"
              onClick={onCancel}
              className="text-xs text-gray-500 hover:text-gray-800 underline underline-offset-2"
            >
              Cancel and return to template list
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
